import React, { Suspense, lazy, use, useCallback, useState } from 'react';
import { Button, Form, Stack, ToggleButton, ToggleButtonGroup } from 'react-bootstrap';
import { Circle, CircleFill, CircleHalf } from 'react-bootstrap-icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { clearAllCachedData } from '../../stores/Store.js';
import { useTranslation } from 'react-i18next';
import { type UserAttributes, type UserSetting } from '@soulhx/fs-common';
import { type ColourMode, handleInit, SHXButton, SidebarCollapseWidget, toColourMode } from '@soulhx/ui-common';
import { ShxHelpText } from './ShxHelpText.js';
import HomeSettings from '../home/HomeSettings.js';
import { useDispatch, useSelector } from 'react-redux';
import {
  getShowAllHelpText,
  updateShowAllHelpText,
  getShowHoverText,
  setShowHoverText,
  useLogoutMutation,
  useUpdateUserBaseMutation,
  useUserSetting,
  useBulkUserSettings,
} from '@soulhx/client-store';
import { HideHelpText } from '../comms/HideHelpText.js';
import { CommsSettings } from '../comms/CommsSettings.js';
import { SoulHXContext, type SoulHXContextType } from '../../stores/SoulHXContext.js';
const PraySettings = lazy(() => import('../pray/PraySettings.js'));
const DoSettings = lazy(() => import('../do/DoSettings.js'));
const ReadSettings = lazy(() => import('../read/ReadSettings.js'));
const PlanSettings = lazy(() => import('../plans/PlanSettings.js'));

const colourModeOptions: { mode: ColourMode; icon: React.ReactElement }[] = [
  { mode: 'light', icon: <Circle /> },
  { mode: 'dark', icon: <CircleFill /> },
  { mode: 'auto', icon: <CircleHalf /> },
];

interface ISettings {
  closeFunc(): void;
}
export const Settings = ({ closeFunc }: ISettings) => {
  const location = useLocation();
  const [logout] = useLogoutMutation();
  const navigate = useNavigate();
  const { settings, settingsError, settingsLoading, bulkUpdate } = useBulkUserSettings();
  const { settingValue: showPrayerSettings, update: updateShowPrayerSettings } =
    useUserSetting<boolean>('prayerShowSettings');
  const { settingValue: showReadSettings, update: updateShowReadSettings } =
    useUserSetting<boolean>('readShowSettings');
  const { settingValue: showSizeIndicator, update: updateShowSizeIndicator } =
    useUserSetting<boolean>('adminShowSizeIndicator');
  const { settingValue: showActionBtns, update: updateShowActionBtns } =
    useUserSetting<boolean>('generalShowActionBtns');
  const { settingValue: showDo, update: updateShowDo } = useUserSetting<boolean>('doShowSettings');
  const { settingValue: showPlan, update: updateShowPlan } = useUserSetting<boolean>('planShowSettings');
  const { settingValue: showComms, update: updateShowComms } = useUserSetting<boolean>('commsShowSettings');
  const { settingValue: showHelpTextReset, update: updateShowHelpTextReset } = useUserSetting<boolean>(
    'generalShowHelpTextResetSetting'
  );
  const { settingValue: showHomeSettings, update: updateShowHomeSettings } =
    useUserSetting<boolean>('homeShowSettings');
  const { user: userData } = use(SoulHXContext) as SoulHXContextType;
  const { t } = useTranslation();
  const [displayNameForField, setDisplayNameForField] = useState(userData.displayName || '');
  const [displayNameUpdated, setDisplayNameUpdated] = useState<boolean>(false);
  const [updateUserApi] = useUpdateUserBaseMutation();
  const { settingValue: numHelpTextsString, update: updateNumHelpTexts } =
    useUserSetting<string>('generalNumHelpTexts');
  const [numHelpTextsForField, setNumHelpTextsForField] = useState(numHelpTextsString);
  const [numHelpTextsUpdated, setNumHelpTextsUpdated] = useState(false);
  const { settingValue: showAnnouncementsOnHome, update: updateShowAnnouncementsOnHome } = useUserSetting<boolean>(
    'generalShowAnnouncementsOnHome'
  );
  const { settingValue: colourModeSetting, update: updateColourMode } = useUserSetting<string>('generalColourMode');
  const colourMode = toColourMode(colourModeSetting);
  const showTooltips = useSelector(getShowHoverText);
  const showAllHelpText = useSelector(getShowAllHelpText);
  const dispatch = useDispatch();

  const navOutOfSettings = useCallback(
    (dest: string) => {
      closeFunc();
      navigate(dest);
    },
    [closeFunc, navigate]
  );

  const resp = handleInit([settingsLoading], [settingsError]);
  if (resp) return resp;

  return (
    <>
      <ShxHelpText pageName="settings" textName="welcomeMsg" variant="success" />

      <h5>{t('settings.title')}</h5>

      <Form.Group className="mb-5">
        <ShxHelpText pageName="settings" textName="displayName" className="mb-0" />
        <Form.Label>{t('settings.fields.displayName.label')}</Form.Label>
        <Form.Control
          type="text"
          value={displayNameForField}
          isValid={displayNameUpdated}
          placeholder={t('settings.fields.displayName.placeholder')}
          onChange={(e) => {
            setDisplayNameForField(e.currentTarget.value);
            setDisplayNameUpdated(false);
          }}
          onBlur={async (e) => {
            if (
              e.currentTarget.value === userData.displayName ||
              (e.currentTarget.value.length < 1 && userData.displayName === undefined)
            )
              return;

            const newUserObj: UserAttributes = {
              ...userData,
              displayName: e.currentTarget.value.length > 0 ? e.currentTarget.value : undefined,
            };

            await updateUserApi(newUserObj).unwrap();
            setDisplayNameUpdated(true);
          }}
        />
        {displayNameUpdated && (
          <Form.Control.Feedback type="valid">{t('settings.fields.displayName.success')}</Form.Control.Feedback>
        )}
      </Form.Group>

      <ShxHelpText pageName="settings" textName="colourMode" className="mb-0" />
      <Form.Group className="mb-5">
        <Form.Label id="settings-colour-mode-label" className="d-block">
          {t('settings.colourMode.label')}
        </Form.Label>
        <ToggleButtonGroup
          type="radio"
          name="settings-colour-mode"
          aria-labelledby="settings-colour-mode-label"
          value={colourMode}
          onChange={(newMode: ColourMode) => updateColourMode(newMode)}
        >
          {colourModeOptions.map(({ mode, icon }) => (
            <ToggleButton key={mode} id={`settings-colour-mode-${mode}`} value={mode} variant="outline-secondary">
              {icon} {t(`settings.colourMode.${mode}`)}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Form.Group>

      <Form.Group className="mb-5">
        <ShxHelpText pageName="settings" textName="numHelpTexts" className="mb-0" />
        <Form.Label>{t('settings.fields.numHelpTexts.label')}</Form.Label>
        <Form.Control
          type="number"
          value={numHelpTextsForField}
          isValid={numHelpTextsUpdated}
          onChange={(e) => {
            if (e.currentTarget.value === numHelpTextsString) return;

            setNumHelpTextsForField(e.currentTarget.value);
            setNumHelpTextsUpdated(false);
          }}
          onBlur={async (e) => {
            if (e.currentTarget.value === numHelpTextsString) return;

            await updateNumHelpTexts(e.currentTarget.value);
            setNumHelpTextsUpdated(true);
          }}
        />
        <Form.Control.Feedback type="valid">{t('settings.fields.numHelpTexts.success')}</Form.Control.Feedback>
      </Form.Group>

      <HideHelpText />

      {userData.isAdmin && (
        <>
          <Form.Group className="mb-5">
            <Form.Check
              type="switch"
              checked={showSizeIndicator}
              label={t('settings.showSizeIndicator')}
              onChange={() => updateShowSizeIndicator(!showSizeIndicator)}
            />
          </Form.Group>

          <Form.Group className="mb-5">
            <Form.Check
              type="switch"
              checked={showAllHelpText}
              label={t('settings.showAllHelpText')}
              onChange={() => dispatch(updateShowAllHelpText(!showAllHelpText))}
            />
          </Form.Group>
        </>
      )}

      <ShxHelpText pageName="settings" textName="showAnnouncementsOnHome" className="mt-5 mb-0" />
      <Form.Group className="mb-5">
        <Form.Check
          type="switch"
          checked={showAnnouncementsOnHome}
          label={t('settings.showAnnouncementsOnHomeScreen')}
          onChange={() => updateShowAnnouncementsOnHome(!showAnnouncementsOnHome)}
        />
      </Form.Group>

      <ShxHelpText pageName="settings" textName="showTooltips" className="mt-5 mb-0" variant="warning" />
      <Form.Group className="mb-5">
        <Form.Check
          type="switch"
          checked={showTooltips}
          label={t('settings.showTooltips')}
          onChange={() => dispatch(setShowHoverText(!showTooltips))}
        />
      </Form.Group>

      <ShxHelpText pageName="settings" textName="showActionBtns" className="mt-5 mb-0" />
      <Form.Group className="mb-5">
        <Form.Check
          type="switch"
          checked={showActionBtns}
          label={t('settings.showActionBtns')}
          onChange={() => updateShowActionBtns(!showActionBtns)}
        />
      </Form.Group>

      <ShxHelpText pageName="settings" textName="collapseWidgets" className="mt-5 mb-1" />

      {location.pathname === '/' && (
        <SidebarCollapseWidget
          title={t('settings.collapseSections.home')}
          visible={showHomeSettings}
          clickFunction={() => updateShowHomeSettings(showHomeSettings ? false : true)}
        >
          <Suspense fallback={'...'}>
            <HomeSettings />
          </Suspense>
        </SidebarCollapseWidget>
      )}

      {location.pathname.startsWith('/pray') && (
        <SidebarCollapseWidget
          title={t('settings.collapseSections.prayer')}
          visible={showPrayerSettings}
          clickFunction={() => updateShowPrayerSettings(showPrayerSettings ? false : true)}
        >
          <Suspense fallback={'...'}>
            <PraySettings />
          </Suspense>
        </SidebarCollapseWidget>
      )}

      {location.pathname.startsWith('/read') && (
        <SidebarCollapseWidget
          title={t('settings.collapseSections.read')}
          visible={showReadSettings}
          clickFunction={() => updateShowReadSettings(showReadSettings ? false : true)}
        >
          <Suspense fallback={'...'}>
            <ReadSettings />
          </Suspense>
        </SidebarCollapseWidget>
      )}

      {location.pathname.startsWith('/do') && (
        <SidebarCollapseWidget
          title={t('settings.collapseSections.do')}
          visible={showDo}
          clickFunction={() => updateShowDo(showDo ? false : true)}
        >
          <Suspense fallback={'...'}>
            <DoSettings closeFn={closeFunc} />
          </Suspense>
        </SidebarCollapseWidget>
      )}

      {location.pathname.startsWith('/plan') && (
        <SidebarCollapseWidget
          title={t('settings.collapseSections.plans')}
          visible={showPlan}
          clickFunction={() => updateShowPlan(showPlan ? false : true)}
        >
          <Suspense fallback={'...'}>
            <PlanSettings />
          </Suspense>
        </SidebarCollapseWidget>
      )}

      {location.pathname.startsWith('/comms') && (
        <SidebarCollapseWidget
          title={t('settings.collapseSections.comms')}
          visible={showComms}
          clickFunction={() => updateShowComms(!showComms)}
        >
          <CommsSettings />
        </SidebarCollapseWidget>
      )}

      <SidebarCollapseWidget
        title={t('settings.showHelpReset')}
        visible={showHelpTextReset}
        clickFunction={() => updateShowHelpTextReset(showHelpTextReset ? false : true)}
      >
        <div>
          <Button
            variant="secondary"
            onClick={() => {
              const itemsToFlip: UserSetting[] = settings
                .filter((s) => s.settingId.startsWith('helpText') && s.settingBoolValue === false)
                .map((s) => ({ settingId: s.settingId, settingBoolValue: true }));
              bulkUpdate(itemsToFlip);
            }}
          >
            {t('settings.resetHelpTextBtn')}
          </Button>
        </div>
      </SidebarCollapseWidget>

      <Stack gap={3} className="my-4">
        <ShxHelpText pageName="settings" textName="tutorialBtn" className="mb-0" />
        <SHXButton variant="primary" onClick={() => navOutOfSettings('/tutorial')}>
          {t('settings.tutorial')}
        </SHXButton>

        <Stack gap={3} className="my-4">
          <ShxHelpText pageName="settings" textName="edTutorialBtn" className="mb-0" />
          <SHXButton variant="primary" onClick={() => navOutOfSettings('/editortutorial')}>
            {t('settings.edTutorial')}
          </SHXButton>
        </Stack>

        <SHXButton variant="secondary" onClick={() => navOutOfSettings('/comms')}>
          {t('settings.showCommsBtn')}
        </SHXButton>

        <SHXButton variant="warning" onClick={() => navOutOfSettings('/changePwd')}>
          {t('settings.changePwd')}
        </SHXButton>

        <SHXButton
          className="mb-3"
          variant="danger"
          onClick={async () => {
            await logout().unwrap();
            await clearAllCachedData();
            window.localStorage.setItem('logout', Date.now().toLocaleString());
            navigate('/');
          }}
        >
          {t('settings.logoutBtn')}
        </SHXButton>
      </Stack>

      <div>
        <Form.Text muted>{t('settings.version', { version: VERSION })}</Form.Text>
      </div>
    </>
  );
};

export default Settings;
